import { useMemo } from "react";
import {
  resolveTimezone,
  toZonedDisplayDate,
} from "../../../../utils/timezone";
import {
  FACEBOOK as FACEBOOK_CONSTANT,
  INSTAGRAM as INSTAGRAM_CONSTANT,
  LINKEDIN as LINKEDIN_CONSTANT,
  META as META_CONSTANT,
  STATES,
  TIKTOK as TIKTOK_CONSTANT,
  YOUTUBE as YOUTUBE_CONSTANT,
} from "../../../../utils/constants";
import { createPlatformDefaultValues } from "../utils/platforms";
import {
  findFacebookAccountOption,
  findInstagramAccountOption,
  findLinkedInAccountOption,
  findTikTokAccountOption,
  findYouTubeAccountOption,
} from "../utils/resolvePlatformAccountOptions";

const PLATFORM_KEY_MAP = {
  [META_CONSTANT]: INSTAGRAM_CONSTANT,
  [FACEBOOK_CONSTANT]: FACEBOOK_CONSTANT,
  [INSTAGRAM_CONSTANT]: INSTAGRAM_CONSTANT,
  [TIKTOK_CONSTANT]: TIKTOK_CONSTANT,
  [YOUTUBE_CONSTANT]: YOUTUBE_CONSTANT,
  linkedin: LINKEDIN_CONSTANT,
};

const normalizePlatformKey = (platformKey = "") => {
  if (typeof platformKey !== "string") return null;
  const normalized = platformKey.trim().toLowerCase();
  return PLATFORM_KEY_MAP[normalized] || null;
};

const normalizePlatformsDataMap = (platformsData = {}) =>
  Object.entries(platformsData || {}).reduce((acc, [key, value]) => {
    const normalizedKey = normalizePlatformKey(key);
    if (normalizedKey) {
      acc[normalizedKey] = value || {};
    }
    return acc;
  }, {});

export const listLockedPublishedPlatforms = (originalData) => {
  const states = originalData?.states || {};
  const locked = [];
  const seen = new Set();

  Object.entries(states).forEach(([platform, state]) => {
    if (state !== STATES.PUBLISHED) return;
    const normalized = normalizePlatformKey(platform);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    locked.push(normalized);
  });

  return locked;
};

export const useShareModalEdit = ({
  isEditing,
  editingData,
  defaultLanguage,
  scheduledDate,
  advertiserTimezone,
  uniqueInstagramBusinessList,
  uniqueFacebookPagesList,
  integrations,
}) => {
  const editingOriginalData = editingData?.originalData || null;

  const editingHydratedState = useMemo(() => {
    if (!isEditing || !editingOriginalData) return null;

    const normalizedPlatforms = Array.from(
      new Set(
        (editingOriginalData.platforms || [])
          .map((platform) => normalizePlatformKey(platform))
          .filter(Boolean)
      )
    );

    const normalizedPlatformsData = normalizePlatformsDataMap(
      editingOriginalData.platformsData || {}
    );

    const platformsData = createPlatformDefaultValues();

    normalizedPlatforms.forEach((platformKey) => {
      const platformData = normalizedPlatformsData[platformKey] || {};

      if (platformKey === INSTAGRAM_CONSTANT) {
        platformsData[INSTAGRAM_CONSTANT] = {
          ...platformsData[INSTAGRAM_CONSTANT],
          account: findInstagramAccountOption(
            platformData,
            uniqueInstagramBusinessList
          ),
          contentType: platformData.mediaType,
          collaborators: Array.isArray(platformData.collaborators)
            ? platformData.collaborators
            : [],
        };
      }

      if (platformKey === FACEBOOK_CONSTANT) {
        platformsData[FACEBOOK_CONSTANT] = {
          ...platformsData[FACEBOOK_CONSTANT],
          account: findFacebookAccountOption(
            platformData,
            uniqueFacebookPagesList
          ),
          contentType: platformData.mediaType,
        };
      }

      if (platformKey === TIKTOK_CONSTANT) {
        platformsData[TIKTOK_CONSTANT] = {
          ...platformsData[TIKTOK_CONSTANT],
          account: findTikTokAccountOption(platformData, integrations),
          privacy_level: platformData.privacy || "",
          disable_duet: Boolean(platformData.allow?.duet),
          disable_stitch: Boolean(platformData.allow?.stitch),
          disable_comment: Boolean(platformData.allow?.comments),
          is_aigc: Boolean(platformData.allow?.is_aigc),
          auto_add_music: Boolean(platformData.allow?.auto_add_music),
        };
      }

      if (platformKey === YOUTUBE_CONSTANT) {
        platformsData[YOUTUBE_CONSTANT] = {
          ...platformsData[YOUTUBE_CONSTANT],
          account: findYouTubeAccountOption(platformData, integrations),
          title: platformData.title || "",
          privacyStatus: platformData.privacyStatus || "",
          notifySubscribers: Boolean(platformData.notifySubscribers),
          madeForKids: Boolean(platformData.madeForKids),
        };
      }

      if (platformKey === LINKEDIN_CONSTANT) {
        platformsData[LINKEDIN_CONSTANT] = {
          ...platformsData[LINKEDIN_CONSTANT],
          account: findLinkedInAccountOption(platformData, integrations),
        };
      }
    });

    const editingTimezone = resolveTimezone(
      editingOriginalData.timezone || advertiserTimezone
    );

    const resolveScheduledDate = () => {
      if (scheduledDate instanceof Date) {
        return Number.isNaN(scheduledDate.getTime()) ? null : scheduledDate;
      }

      if (editingOriginalData.scheduledDate) {
        const parsed = toZonedDisplayDate({
          date: editingOriginalData.scheduledDate,
          timezone: editingTimezone,
        });
        return parsed;
      }

      return null;
    };

    return {
      formValues: {
        language: editingOriginalData.language || defaultLanguage,
        description: editingOriginalData.description || "",
        scheduledDate: resolveScheduledDate(),
        timezone: editingTimezone,
        platformsData,
        platforms: normalizedPlatforms,
      },
      selectedPlatforms: normalizedPlatforms,
    };
  }, [
    isEditing,
    editingOriginalData,
    defaultLanguage,
    scheduledDate,
    advertiserTimezone,
    uniqueInstagramBusinessList,
    uniqueFacebookPagesList,
    integrations,
  ]);

  const lockedPublishedPlatforms = useMemo(
    () =>
      isEditing ? listLockedPublishedPlatforms(editingOriginalData) : [],
    [isEditing, editingOriginalData]
  );

  return {
    editingOriginalData,
    editingHydratedState,
    lockedPublishedPlatforms,
  };
};

export default useShareModalEdit;
